import { defineStore } from "pinia";
import { ref } from "vue";
import { IPowderType } from "../models/powderType";
import { IPowderTypeApiResponse } from "../models/api/powderTypeApiResponse";
import { IPowderTypeFilter } from "../models/filters/powderTypeFilter";
import PowderTypeService from "../services/PowderTypeService";
import { IPowderTypeForm } from "../models/forms/powderTypeForm";
import { ICenter } from "../models/center";
import { ServiceOperationResult } from "../models/api/serviceOperationResult";
import DataOperationError from "../models/api/dataOperationError";
import { IDataOperationResult } from "../models/api/dataOperationResult";
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";

export const usePowderTypeStore = defineStore("powderTypes", () => {
    // State
    const datas = ref<IPowderType[]>([]);
    const current = ref<IPowderType>({} as IPowderType);
    const itemForm = ref<IPowderTypeForm>({} as IPowderTypeForm);

    // Actions - Hydration
    async function hydratePowderTypes(items: IPowderTypeApiResponse[]): Promise<IPowderType[]> {
        if (items.length === 0) return [];
        return items.map(item => ({ ...item } as unknown as IPowderType));
    }

    // Actions
    async function load(filter: IPowderTypeFilter) {
        let result = await PowderTypeService.getAll<IPowderTypeFilter, IPowderTypeApiResponse>(filter);
        datas.value = [];
        if (!result.error) {
            datas.value = await hydratePowderTypes(result.data as IPowderTypeApiResponse[]);
        }
        return result;
    }

    async function get(id: number) {
        let result = await PowderTypeService.getById<IPowderTypeApiResponse>(id);
        if (!result.error) {
            const [modelData] = await hydratePowderTypes([result.data as IPowderTypeApiResponse]);
            let index = datas.value.findIndex((a) => a.id == id);
            if (index >= 0)
                datas.value[index] = modelData;
            current.value = modelData;
            itemForm.value = await toForm(modelData);
        }
        return result;
    }

    async function searchStoreById(id: number) {
        current.value = datas.value.find((objet) => objet.id == id)!;
        itemForm.value = await toForm(current.value);
        return current.value;
    }

    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result = {} as ServiceOperationResult<IPowderType | IDataOperationResult<IPowderType>>;
        if (itemForm.value.id > 0) {
            result = await PowderTypeService.update(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydratePowderTypes([result.data as IPowderTypeApiResponse]);
                current.value = modelData;
                if (maj) {
                    let index = datas.value.findIndex((a) => a.id == itemForm.value.id);
                    if (index >= 0) {
                        datas.value[index] = current.value;
                    } else {
                        const error = new DataOperationError();
                        error.REQUEST_ERRROR = "Item non trouvé dans le store";
                        return new ServiceOperationResult<IDataOperationResult<IPowderType>>(true, { errors: error });
                    }
                }
            }
        } else {
            result = await PowderTypeService.create(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydratePowderTypes([result.data as IPowderTypeApiResponse]);
                current.value = modelData;
            }
        }
        return result;
    }

    async function toForm(item: IPowderType): Promise<IPowderTypeForm> {
        const form = {} as IPowderTypeForm;
        Object.assign(form, item);
        return form;
    }

    function newForm(centerId: number): IPowderTypeForm {
        itemForm.value = {
            id: 0,
            name: "",
            limitMin: undefined,
            limitMax: undefined
        } as IPowderTypeForm;
        return itemForm.value;
    }

    function newItem(center: ICenter): IPowderType {
        current.value = {
            id: 0,
            name: "",
            limitMin: undefined,
            limitMax: undefined
        } as IPowderType;
        return current.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,
        // Actions
        hydratePowderTypes,
        load,
        get,
        searchStoreById,
        save,
        toForm,
        newForm,
        newItem
    };
});

signalRManager.connection.on("MajPowderType", async (payload: IPayloadStore) => {
    const store = usePowderTypeStore();
    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == payload.data?.id) == -1) {
                const [hydrated] = await store.hydratePowderTypes([payload.data as IPowderTypeApiResponse]);
                store.datas.push(hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == payload.data?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydratePowderTypes([payload.data as IPowderTypeApiResponse]);
                store.datas.splice(idx, 1, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});
